import { Image03 } from "@untitledui/icons";
import { cx } from "class-variance-authority";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";

type SampleProduct = {
  skuImageUrl: string | null;
  skuName: string | null;
};

type Props = {
  products: SampleProduct[];
  maxVisible?: number;
  size?: "sm" | "md";
};

/**
 * Renders overlapping product SKU thumbnails similar to an avatar stack.
 * Shows a "+N" indicator when there are more products than maxVisible.
 */
export const SampleProductThumbnails = ({ products, maxVisible = 3, size = "sm" }: Props) => {
  // Filter out products without images
  const productsWithImages = products.filter((p) => p.skuImageUrl);

  if (productsWithImages.length === 0) {
    return <span className="text-sm text-tertiary">--</span>;
  }

  const visibleProducts = productsWithImages.slice(0, maxVisible);
  const remainingCount = productsWithImages.length - maxVisible;

  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
  };

  const overlapClasses = {
    sm: "-ml-2",
    md: "-ml-3",
  };

  return (
    <div className="flex items-center">
      {visibleProducts.map((product, index) => (
        <Tooltip key={product.skuImageUrl ?? index} title={product.skuName || "Product"} placement="top">
          <TooltipTrigger>
            <div
              className={cx(
                "rounded-md border border-secondary bg-bg-secondary overflow-hidden flex-shrink-0 relative",
                sizeClasses[size],
                index > 0 && overlapClasses[size],
              )}
              style={{ zIndex: visibleProducts.length - index }}
            >
              <img
                src={product.skuImageUrl || ""}
                alt={product.skuName || "Product thumbnail"}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
              <div className="absolute inset-0 hidden items-center justify-center bg-bg-secondary">
                <Image03 className="w-4 h-4 text-fg-quaternary" />
              </div>
            </div>
          </TooltipTrigger>
        </Tooltip>
      ))}

      {remainingCount > 0 && (
        <div
          className={cx(
            "rounded-md border border-secondary bg-tertiary flex items-center justify-center text-xs font-medium text-tertiary flex-shrink-0",
            sizeClasses[size],
            overlapClasses[size],
          )}
          style={{ zIndex: 0 }}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
};
